/**
 * RelatedClusters — A5 criterion 8 (cross-cluster internal mesh).
 *
 * Every deep content page must link out to at least two OTHER service
 * clusters with descriptive, non-stuffed anchors. This component renders that
 * mesh for locale mirrors, using only routes that exist for every locale
 * (`/{locale}/naati`, `/notary`, `/pcc`, `/moj`, `/sworn`, `/visa`, `/lgz`,
 * `/services`), so no link can ever point at a 404.
 *
 * Presentation only: no data fetching, no business logic.
 */

export type MeshLocale = "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";

/** Cluster keys shared by every locale. */
export type ClusterKey = "naati" | "notary" | "pcc" | "moj" | "sworn" | "visa" | "lgz" | "services";

type Entry = { label: string; blurb: string };

const HEADING: Record<MeshLocale, string> = {
  th: "บริการที่เกี่ยวข้อง",
  en: "Related services",
  zh: "相关服务",
  ja: "関連サービス",
  ko: "관련 서비스",
  vi: "Dịch vụ liên quan",
  id: "Layanan terkait",
};

const COPY: Record<MeshLocale, Record<ClusterKey, Entry>> = {
  th: {
    naati: { label: "แปลเอกสารรับรอง NAATI", blurb: "สำหรับยื่นหน่วยงานออสเตรเลียและนิวซีแลนด์" },
    notary: { label: "รับรองเอกสารโดยทนาย Notary Public", blurb: "รับรองลายมือชื่อ สำเนา และคำสาบานสำหรับใช้ต่างประเทศ" },
    pcc: { label: "หนังสือรับรองความประพฤติ (ตำรวจ)", blurb: "ขั้นตอนขอ ตรวจลายพิมพ์นิ้วมือ และการรับรองต่อเนื่อง" },
    moj: { label: "รับรองเอกสารกระทรวงยุติธรรม", blurb: "เอกสารคดี ทะเบียนราษฎร และการรับรองคำแปล" },
    sworn: { label: "นักแปลผู้ให้คำสาบาน (Sworn Translator)", blurb: "คำแปลพร้อมคำรับรองของผู้แปลสำหรับหน่วยงานยุโรป" },
    visa: { label: "แปลเอกสารสำหรับยื่นวีซ่า", blurb: "ชุดเอกสารตามประเทศปลายทางและประเภทวีซ่า" },
    lgz: { label: "รับรองนิติกรณ์ กรมการกงสุลและสถานทูต", blurb: "ลำดับขั้นการรับรองก่อนใช้ในต่างประเทศ" },
    services: { label: "ดูบริการทั้งหมด", blurb: "ภาพรวมบริการเอกสาร คำแปล และการรับรอง" },
  },
  en: {
    naati: { label: "NAATI certified translation", blurb: "For submissions to Australian and New Zealand authorities" },
    notary: { label: "Notarial services by a Thai attorney", blurb: "Signature, copy and affidavit certification for overseas use" },
    pcc: { label: "Police clearance certificate", blurb: "Application steps, fingerprinting and onward certification" },
    moj: { label: "Ministry of Justice certification", blurb: "Court, civil-registry and translation certification routes" },
    sworn: { label: "Sworn translator statements", blurb: "Translator declarations required by European authorities" },
    visa: { label: "Visa document translation", blurb: "Document sets by destination country and visa stream" },
    lgz: { label: "Consular and embassy legalisation", blurb: "The certification order before a document is used abroad" },
    services: { label: "All services", blurb: "Overview of document, translation and certification work" },
  },
  zh: {
    naati: { label: "NAATI 认证翻译", blurb: "用于向澳大利亚与新西兰机构提交" },
    notary: { label: "泰国律师公证服务", blurb: "签字、副本与宣誓书认证，供境外使用" },
    pcc: { label: "警察无犯罪记录证明", blurb: "申请流程、指纹采集与后续认证" },
    moj: { label: "司法部认证", blurb: "法院、户籍与译文认证途径" },
    sworn: { label: "宣誓译员声明", blurb: "欧洲机构要求的译员声明" },
    visa: { label: "签证材料翻译", blurb: "按目的地国家与签证类别整理材料" },
    lgz: { label: "领事与使馆认证", blurb: "文件在境外使用前的认证顺序" },
    services: { label: "全部服务", blurb: "文件、翻译与认证服务总览" },
  },
  ja: {
    naati: { label: "NAATI 認定翻訳", blurb: "豪州・ニュージーランド当局への提出用" },
    notary: { label: "タイ弁護士による公証業務", blurb: "署名・写し・宣誓書の認証（海外提出用）" },
    pcc: { label: "無犯罪証明書（警察）", blurb: "申請手順、指紋採取、その後の認証" },
    moj: { label: "法務省の認証", blurb: "裁判所・戸籍・翻訳文の認証ルート" },
    sworn: { label: "宣誓翻訳者の証明", blurb: "欧州の機関が求める翻訳者宣誓文" },
    visa: { label: "ビザ申請書類の翻訳", blurb: "提出先国とビザ種別ごとの書類構成" },
    lgz: { label: "領事・大使館認証", blurb: "海外使用前の認証の順序" },
    services: { label: "すべてのサービス", blurb: "書類・翻訳・認証業務の概要" },
  },
  ko: {
    naati: { label: "NAATI 인증 번역", blurb: "호주·뉴질랜드 기관 제출용" },
    notary: { label: "태국 변호사 공증 업무", blurb: "서명·사본·진술서 인증(해외 제출용)" },
    pcc: { label: "범죄경력증명서(경찰)", blurb: "신청 절차, 지문 채취, 후속 인증" },
    moj: { label: "법무부 인증", blurb: "법원·가족관계·번역문 인증 경로" },
    sworn: { label: "선서 번역사 확인서", blurb: "유럽 기관이 요구하는 번역사 선서문" },
    visa: { label: "비자 서류 번역", blurb: "목적국과 비자 종류별 서류 구성" },
    lgz: { label: "영사·대사관 인증", blurb: "해외 사용 전 인증 순서" },
    services: { label: "전체 서비스", blurb: "서류·번역·인증 업무 개요" },
  },
  vi: {
    naati: { label: "Dịch thuật NAATI", blurb: "Dùng để nộp cho cơ quan Úc và New Zealand" },
    notary: { label: "Chứng nhận bởi luật sư Notary Public Thái", blurb: "Chứng nhận chữ ký, bản sao và bản khai để dùng ở nước ngoài" },
    pcc: { label: "Lý lịch tư pháp cảnh sát", blurb: "Quy trình xin, lấy dấu vân tay và chứng nhận tiếp theo" },
    moj: { label: "Chứng nhận Bộ Tư pháp", blurb: "Hồ sơ toà án, hộ tịch và chứng nhận bản dịch" },
    sworn: { label: "Tuyên thệ của dịch giả", blurb: "Bản tuyên thệ dịch giả theo yêu cầu của cơ quan châu Âu" },
    visa: { label: "Dịch hồ sơ xin visa", blurb: "Bộ hồ sơ theo quốc gia đến và loại visa" },
    lgz: { label: "Hợp pháp hoá lãnh sự và đại sứ quán", blurb: "Trình tự chứng nhận trước khi dùng ở nước ngoài" },
    services: { label: "Tất cả dịch vụ", blurb: "Tổng quan giấy tờ, dịch thuật và chứng nhận" },
  },
  id: {
    naati: { label: "Terjemahan bersertifikat NAATI", blurb: "Untuk pengajuan ke instansi Australia dan Selandia Baru" },
    notary: { label: "Layanan notarial oleh advokat Thailand", blurb: "Pengesahan tanda tangan, salinan, dan pernyataan untuk penggunaan di luar negeri" },
    pcc: { label: "Surat keterangan catatan kepolisian", blurb: "Alur pengajuan, sidik jari, dan pengesahan lanjutan" },
    moj: { label: "Pengesahan Kementerian Kehakiman", blurb: "Jalur pengesahan dokumen pengadilan, catatan sipil, dan terjemahan" },
    sworn: { label: "Pernyataan penerjemah tersumpah", blurb: "Pernyataan penerjemah yang diminta instansi Eropa" },
    visa: { label: "Terjemahan dokumen visa", blurb: "Susunan dokumen menurut negara tujuan dan jenis visa" },
    lgz: { label: "Legalisasi konsuler dan kedutaan", blurb: "Urutan pengesahan sebelum dokumen dipakai di luar negeri" },
    services: { label: "Semua layanan", blurb: "Ikhtisar dokumen, terjemahan, dan pengesahan" },
  },
};

const DEFAULT_ORDER: ClusterKey[] = ["notary", "naati", "sworn", "lgz", "pcc", "moj", "visa", "services"];

function href(locale: MeshLocale, key: ClusterKey) {
  const path = key === "services" ? "/services" : `/${key}`;
  return locale === "th" ? path : `/${locale}${path}`;
}

const KNOWN = new Set<string>(DEFAULT_ORDER);

/** Accepts a single key or a list; drops anything that is not a real cluster. */
function toKeys(value: ClusterKey | string | readonly (ClusterKey | string)[] | undefined): ClusterKey[] {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.filter((v): v is ClusterKey => typeof v === "string" && KNOWN.has(v));
}

export function RelatedClusters({
  locale = "th",
  exclude,
  only,
  items,
  theme,
  limit = 4,
  className,
}: {
  locale?: MeshLocale;
  /** Cluster(s) to omit — normally the cluster the current page belongs to. */
  exclude?: ClusterKey[] | ClusterKey | string | string[];
  /** Explicit ordering when a page wants a specific mesh. */
  only?: ClusterKey[] | string[];
  /** Alias of `only`, kept for existing call sites. */
  items?: ClusterKey[] | string[];
  /**
   * Owning cluster of the host page. Used only to exclude itself; unknown
   * themes such as "store" or "marriage" are ignored safely.
   */
  theme?: ClusterKey | string;
  limit?: number;
  className?: string;
}) {
  const copy = COPY[locale] ?? COPY.th;
  const skip = new Set<ClusterKey>([...toKeys(exclude), ...toKeys(theme)]);
  const order = toKeys(only ?? items);
  const keys = (order.length ? order : DEFAULT_ORDER)
    .filter((k) => !skip.has(k))
    .slice(0, Math.max(2, limit));
  if (keys.length < 2) return null;


  return (
    <nav
      className={`not-prose mt-10 rounded-xl border border-border/60 bg-muted/20 p-5 ${className ?? ""}`}
      aria-label={HEADING[locale] ?? HEADING.th}
    >
      <h2 className="m-0 mb-3 text-lg font-bold text-foreground">{HEADING[locale] ?? HEADING.th}</h2>
      <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2">
        {keys.map((k) => (
          <li key={k} className="m-0">
            <a href={href(locale, k)} className="font-semibold text-primary underline-offset-2 hover:underline">
              {copy[k].label}
            </a>
            <p className="m-0 mt-1 text-sm text-muted-foreground">{copy[k].blurb}</p>
          </li>
        ))}
      </ul>
    </nav>
  );
}
